/**
 * A quick look at an MP4 / MOV file's top-level boxes, so a cut-off download is turned away the moment it is
 * added instead of after a minute of encoding that ends in nothing. Reads a few bytes per box (never the whole
 * file), so it is instant even for multi-gigabyte videos.
 *
 * Only files that start like MP4/MOV (an "ftyp" box) are judged; every other container returns null (no opinion).
 * A file is called damaged when a box runs past the end of the file, or the walk ends without finding the
 * "moov" box that holds the index every player needs.
 */
type Slicer = { size: number; slice: (start: number, end: number) => { arrayBuffer: () => Promise<ArrayBuffer> } };

const text4 = (b: Uint8Array, at: number) => String.fromCharCode(b[at], b[at + 1], b[at + 2], b[at + 3]);

export async function findMp4Problem(file: Slicer): Promise<string | null> {
  const read = async (start: number, len: number) => new Uint8Array(await file.slice(start, Math.min(file.size, start + len)).arrayBuffer());
  if (file.size < 16) return "This file is too small to be a video.";
  const head = await read(0, 12);
  if (text4(head, 4) !== "ftyp") return null; // not MP4/MOV: no opinion

  let pos = 0;
  let sawMoov = false;
  let guard = 0;
  while (pos < file.size && guard++ < 2000) {
    const h = await read(pos, 16);
    if (h.length < 8) break;
    const view = new DataView(h.buffer, h.byteOffset, h.byteLength);
    let size = view.getUint32(0);
    const type = text4(h, 4);
    if (size === 1) {
      if (h.length < 16) break;
      size = view.getUint32(8) * 2 ** 32 + view.getUint32(12);
    } else if (size === 0) {
      size = file.size - pos; // box runs to the end of the file
    }
    if (size < 8) return "This video file is damaged (its structure can't be read).";
    if (type === "moov") sawMoov = true;
    if (pos + size > file.size) {
      return sawMoov
        ? null // media data cut short but the index is intact: players can still open it
        : "This video file looks incomplete or damaged (it was cut off before the end).";
    }
    pos += size;
  }
  return sawMoov ? null : "This video file looks incomplete or damaged (it has no index, so it can't be played).";
}
